import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {
  convertUserTimeZone,
  formatOmitLongText,
  formatPhoneNumber,
  getShipmentFees,
  KTSVG,
  setLoadingActionTable,
  useDisabled,
} from '../../../../_gori/helpers'
import {
  Button,
  CarrierBadge,
  ConfirmActionSwal,
  DropdownButton,
  Table,
  TableBody,
  TableWrapper,
  ValidationErrorModal,
} from '../../../../_gori/partials/widgets'
import ShipmentService from '../core/_requests'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {CSSTransition} from 'react-transition-group'
import moment from 'moment'
import {
  CUSTOM_COLUMN_NAME,
  DEFAULT_DATE_FILTER,
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  OUTER_HEIGHT_SHIPMENT,
  SHIPMENT_PADDING_HEIGHT,
  TABLE_KEY_COLUMN,
} from '../../../../_gori/constants'
import {useAuth} from '../../auth'
import _, {cloneDeep, isEmpty, startCase} from 'lodash'
import {ShipmentRefund, ShipmentRefundLabel} from '../core/_const'
import {blockPopUp} from '../../../../_gori/helpers/BrowserHelpers'
import clsx from 'clsx'
import {ParcelBadge} from '../../../../_gori/partials/widgets/mixed/ParcelBadge'
import {toast} from 'react-toastify'
import {ShipmentFilter} from './ShipmentFilter'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import {FilterOrderSkeleton, TableSkeleton} from '../../../../_gori/helpers/components'
import {useStatesGlobal} from '../../../../_gori/helpers/components/StatesGlobalProvider'
import UseRoutesPermission from '../../../../_gori/hooks/UseRoutesPermission'
import {TrackingNumberClaimModal} from '../../claims'
import {useMeter} from '../../../../_gori/helpers/components/MeterProvider'
import {showArrCustomColumns} from '../../../../_gori/utils/common'
import SettingsService from '../../settings/core/_requests'
import {NoticePrintPackingSlips, PackageInfoModal} from '../../orders'
import {CopyToClipboard} from 'react-copy-to-clipboard'
import {ROUTES} from '../../../../_gori/constants/_routes'
import {TrackingNumbersModal} from '../modals/TrackingNumbersModal'
import {getParcelCount, getTotalWeight, getWeightUnit} from '../../../../_gori/utils'
import {UseTopAlert} from '../../../../_gori/hooks/UseTopAlert'
import {EmptyTable} from '../../common/components/EmptyTable'
import {isCreatedWithinLast30Days} from '../../orders/core/_helpers'

const ShipmentPage: React.FC = () => {
  const {setMeterChange} = useMeter()
  const intl = useIntl()
  const navigate = useNavigate()
  const {newCancelToken, isCancel} = useCancelToken()
  const {loadingSwitch, currentUser} = useAuth()
  const {setPageDisabled} = useDisabled()
  const [searchParams] = useSearchParams()
  const {statesGlobal} = useStatesGlobal()
  const {topAlertHeight} = UseTopAlert()

  const columnsShow = statesGlobal?.manageColumns?.[CUSTOM_COLUMN_NAME.SHIPMENT_PAGE]
  const carriers = statesGlobal.carriers
  const [loadingFirst, setLoadingFirst] = useState<Boolean>(true)
  const [responseData, setResponseData] = useState<any>({})
  const [tableData, setTableData] = useState<any>([])
  const [selectedListId, setSelectedListId] = useState<any>([])
  const [validationErrors, setValidationErrors] = useState<any>()
  const [settings, setSettings] = useState<any>([])
  const {routes} = UseRoutesPermission()
  const [showModal, setShowModal] = useState<{
    confirmActionSwal: 'REQUEST_REFUND' | undefined
    createClaims: boolean
    noticePrintPackingSlip: boolean
    trackingNumbers: boolean
    packageInfoTable: boolean
  }>({
    confirmActionSwal: undefined,
    createClaims: false,
    noticePrintPackingSlip: false,
    trackingNumbers: false,
    packageInfoTable: false,
  })
  const [dataModal, setDataModal] = useState<{
    confirmActionSwal: any
    createClaims: any
    noticePrintPackingSlip: any
    trackingNumbers: any
    packageInfoTable: any
  }>({
    confirmActionSwal: null,
    createClaims: {},
    noticePrintPackingSlip: {},
    trackingNumbers: null,
    packageInfoTable: null,
  })

  const initFilterValue = useMemo(
    () => ({
      batch_id: '',
      start: moment().subtract(DEFAULT_DATE_FILTER.SHIPMENTS, 'days').startOf('day').format(),
      end: moment().endOf('day').format(),
    }),
    []
  )

  const emptyStateContent = useMemo(() => {
    if (responseData?.existed_shipment === false) {
      return (
        <EmptyTable
          className='mb-8'
          label='YOU_HAVE_NO_SHIPMENTS_TO_VIEW'
          CTA={{
            title: 'GO_TO_ORDER_PAGE',
            onClick: () => navigate(ROUTES.ORDERS.BASE),
          }}
        />
      )
    }
    return (
      <EmptyTable
        className='mb-8'
        imgSrc='/media/gori/orders/empty-box.svg'
        label='NO_DATA_FOUND'
      />
    )
  }, [responseData?.existed_shipment, navigate])

  const handlePrintLabel = useCallback(
    async (shipment: any) => {
      const data = {
        shipments: [
          {
            gori_shipment_id: shipment.id,
            label: shipment.label.image_url,
          },
        ],
      }
      setTableData(setLoadingActionTable(tableData, [shipment.id], true))
      await ShipmentService.print(data, {cancelToken: newCancelToken()})
        .then((response) => {
          if (blockPopUp(response.label_url)) {
            toast.warning(
              intl.formatMessage({
                id: 'POP_UP_IS_BLOCKED',
              })
            )
          }
        })
        .catch((error: any) => {
          if (isCancel(error)) return
          setValidationErrors(error?.response)
        })
        .finally(() => {
          setTableData(setLoadingActionTable(tableData, [shipment.id], false))
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCancel, newCancelToken, intl, tableData]
  )

  const handlePrintPackingSlip = useCallback(
    async (shipment: any) => {
      const {
        packing_slip: {disable},
      } = settings
      if (disable) {
        const configNoticePackingSlip = {
          title: 'PRINT_PACKING_SLIP',
          content: 'DISABLE_PACKING_SLIPS_MESSAGE',
        }
        setDataModal((prev) => ({...prev, noticePrintPackingSlip: configNoticePackingSlip}))
        setShowModal((prev) => ({...prev, noticePrintPackingSlip: true}))
        return
      }

      setPageDisabled(true)
      const payload = {
        gori_shipment_id: shipment.id,
        order_id: shipment.order_id ?? null,
      }
      try {
        const res = await ShipmentService.storePackingSlip(payload, {cancelToken: newCancelToken()})
        if (res) {
          if (blockPopUp(res.packing_slip_url)) {
            toast.warning(intl.formatMessage({id: 'POP_UP_IS_BLOCKED'}))
          }
        }
      } catch (error) {
        if (isCancel(error)) return
      } finally {
        setPageDisabled(false)
      }
    },
    [settings, setPageDisabled, newCancelToken, intl, isCancel]
  )

  const setTable = useCallback(async () => {
    setPageDisabled(true)

    const config = {
      params: {
        page: searchParams.get('page') || DEFAULT_PAGE,
        page_size: searchParams.get('page_size') || DEFAULT_PAGE_SIZE,
        search_text: searchParams.get('search_text'),
        date_from: searchParams.get('date_from') || initFilterValue.start,
        date_to: searchParams.get('date_to') || initFilterValue.end,
        batch_id: searchParams.get('batch_id') || initFilterValue.batch_id,
      },
      cancelToken: newCancelToken(),
    }
    await ShipmentService.search(config)
      .then((response) => {
        let shipments = Array.from(response.shipments, (shipment: any) => {
          return {
            ...shipment,
            canCheck: true,
          }
        })
        setTableData(shipments)
        setResponseData(response)
      })
      .catch((error) => {
        if (isCancel(error)) return
        setValidationErrors(error?.response)
      })
      .finally(() => {
        setPageDisabled(false)
        setLoadingFirst(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initFilterValue, isCancel, newCancelToken, searchParams, setPageDisabled])

  const handleRefundLabel = useCallback(
    async (shipmentId: any) => {
      setTableData(setLoadingActionTable(tableData, [shipmentId], true))
      await ShipmentService.refund(shipmentId, {}, {cancelToken: newCancelToken()})
        .then(() => {
          setMeterChange(true)
          setTable()
        })
        .catch((error) => {
          if (isCancel(error)) return
          setValidationErrors(error?.response)
        })
        .finally(() => {
          setTableData(setLoadingActionTable(tableData, [shipmentId], false))
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCancel, newCancelToken, setMeterChange, setTable, tableData]
  )

  const handleViewOrder = useCallback(
    (orderId: any) => {
      navigate(`${ROUTES.ORDERS.BASE}/${orderId}`)
    },
    [navigate]
  )

  const columns = useMemo(
    () => [
      {
        id: 'canCheck',
        Header: intl.formatMessage({id: 'ID'}),
        accessor: 'canCheck',
      },
      {
        id: 'order_date',
        Header: intl.formatMessage({id: 'ORDER_DATE'}),
        headerClassName: 'min-w-50px',
        Cell: ({row}: {row: any}) => {
          return row.original?.order_date
            ? convertUserTimeZone(
                row.original?.order_date,
                currentUser,
                process.env.REACT_APP_DATE_FORMAT
              )
            : ''
        },
      },
      {
        id: 'bae_id',
        Header: 'BAE ID',
        headerClassName: 'min-w-100px',
        cellClassName: 'text-wrap',
        Cell: ({row}: {row: any}) => {
          return row.original.order_code ? (
            <div
              className={clsx(
                'text-gray-800 mb-1 d-inline',
                routes.ORDERS_UPDATE.hasPermission
                  ? 'text-hover-primary cursor-pointer'
                  : 'cursor-no-drop'
              )}
              onClick={() =>
                routes.ORDERS_UPDATE.hasPermission ? handleViewOrder(row.original.order_id) : false
              }
            >
              {row.original?.order_code}
            </div>
          ) : (
            ''
          )
        },
      },
      {
        id: 'ship_date',
        Header: intl.formatMessage({id: 'SHIP_DATE'}),
        headerClassName: 'min-w-50px',
        cellClassName: 'text-gray-800',
        Cell: ({row}: {row: any}) => {
          return convertUserTimeZone(
            row.original?.ship_date || row.original?.created_at,
            currentUser,
            process.env.REACT_APP_DATE_FORMAT
          )
        },
      },
      {
        id: 'ship_from',
        Header: intl.formatMessage({id: 'SHIP_FROM'}),
        headerClassName: 'min-w-100px',
        cellClassName: 'text-wrap mw-150px',
        Cell: ({row}: {row: any}) => {
          return (
            <div className='d-flex flex-column'>
              <span className='text-gray-800'>
                {_.isEmpty(row.original.from_address?.company)
                  ? row.original.from_address?.name
                  : row.original.from_address?.company}
              </span>
              <span>
                {row.original.from_address?.city + ', ' + row.original.from_address?.state}
              </span>
            </div>
          )
        },
      },
      {
        id: 'customer',
        Header: intl.formatMessage({id: 'CUSTOMER'}),
        headerClassName: 'min-w-100px',
        cellClassName: 'text-wrap mw-150px',
        Cell: ({row}: {row: any}) => {
          return (
            <div className='d-flex flex-column'>
              <span className='text-gray-800'>
                {_.isEmpty(row.original.to_address?.company)
                  ? row.original.to_address?.name
                  : row.original.to_address?.company}
              </span>
              <span>{row.original.to_address?.city + ', ' + row.original.to_address?.state}</span>
            </div>
          )
        },
      },
      {
        id: 'package_info',
        Header: intl.formatMessage({id: 'PACKAGE_INFO'}),
        headerClassName: 'min-w-100px text-center',
        cellClassName: 'text-wrap text-center',
        Cell: ({row}: {row: any}) => {
          const trackingNumbers = row.original?.tracking_numbers ?? null
          const isMultiParcel = trackingNumbers && row.original?.tracking_numbers.length > 1

          // Obtain the shipment parcels information
          if (isMultiParcel) {
            const countShipmentParcels = getParcelCount(row.original?.parcel)
            const totalShipmentWeight = getTotalWeight(row.original?.parcel)
            const weightUnit = getWeightUnit(row.original?.parcel)
            return (
              <div>
                <div className='badge badge-light'>
                  <span
                    className='cursor-pointer mx-autofw-bold fs-8 bagde badge-light py-1 px-3 rounded-1 text-lowercase lh-sm'
                    onClick={() => {
                      setDataModal((prev) => ({
                        ...prev,
                        packageInfoTable: row.original.parcel,
                      }))
                      setShowModal((prev) => ({...prev, packageInfoTable: true}))
                    }}
                  >
                    {countShipmentParcels} {intl.formatMessage({id: 'PACKAGES'})} <br />
                    <span>
                      {totalShipmentWeight +
                        ' ' +
                        weightUnit +
                        ' ' +
                        intl.formatMessage({id: 'TOTAL'})}
                    </span>
                  </span>
                </div>
              </div>
            )
          } else {
            return (
              <>
                {!isMultiParcel && (
                  <ParcelBadge key={_.uniqueId('parcel')} values={row.original?.parcel} />
                )}
              </>
            )
          }
        },
      },
      {
        id: 'rate',
        Header: intl.formatMessage({id: 'RATE'}),
        headerClassName: 'min-w-50px text-center',
        Cell: ({row}: {row: any}) => {
          let sum = 0
          if (row.original.fees) {
            sum = getShipmentFees(row.original.fees)
          }
          let service = carriers[row.original.carrier + '_' + row.original.service] ?? {
            carrier: row.original.carrier,
            service: row.original.service,
            display_name: `${row.original.carrier.toUpperCase()} ${startCase(
              row.original.service
            )}`,
          }

          return <CarrierBadge service={service} cost={sum} />
        },
      },
      {
        id: 'tracking_number',
        Header: intl.formatMessage({id: 'TRACKING_NUMBER'}),
        headerClassName: 'min-w-125px',
        cellClassName: 'text-wrap mw-200px',
        Cell: ({row}: {row: any}) => {
          let service = carriers[row.original.carrier + '_' + row.original.service] ?? []
          const trackingNumbers = row.original?.tracking_numbers ?? null
          const isMultiParcel = trackingNumbers && row.original?.tracking_numbers.length > 1

          return (
            <>
              {isMultiParcel && (
                <span
                  className='text-primary cursor-pointer'
                  onClick={() => {
                    setShowModal((prev) => ({...prev, trackingNumbers: true}))
                    setDataModal((prev) => ({
                      ...prev,
                      trackingNumbers: row.original,
                    }))
                  }}
                >
                  {intl.formatMessage({id: 'TRACKING_NUMBERS'})}
                  <KTSVG path='/media/gori/orders/external_link.svg' className='svg-icon ps-1' />
                </span>
              )}

              {!isMultiParcel && (
                <div className={'d-flex justify-content-start align-items-center'}>
                  <span
                    className='text-primary cursor-pointer'
                    onClick={() => {
                      if (blockPopUp(service?.visuals?.tracking_url + row.original.tracking_code)) {
                        toast.warning(intl.formatMessage({id: 'POP_UP_IS_BLOCKED'}))
                      }
                    }}
                    title={row.original?.tracking_code}
                  >
                    {formatOmitLongText(row.original?.tracking_code)}
                  </span>
                  <CopyToClipboard
                    text={row.original?.tracking_code}
                    onCopy={() => toast.success(intl.formatMessage({id: 'COPIED'}))}
                  >
                    <div>
                      <div className='btn btn-active-color-primary btn-icon btn-sm btn-outline-light'>
                        <KTSVG path='/media/gori/account/copy.svg' className='svg-icon' />
                      </div>
                    </div>
                  </CopyToClipboard>
                </div>
              )}
            </>
          )
        },
      },
      {
        id: 'order_ref',
        Header: intl.formatMessage({id: 'ORDER_REF'}),
        headerClassName: 'min-w-50px',
        Cell: ({row}: {row: any}) => {
          return (
            <div className='d-flex align-items-center align-middle'>
              <div className='d-flex flex-column'>
                <span className='text-gray-800 mb-1'>{row.original?.reference_1}</span>
              </div>
            </div>
          )
        },
      },
      {
        id: TABLE_KEY_COLUMN.ACTIONS,
        Header: intl.formatMessage({id: 'ACTIONS'}),
        headerClassName: 'min-w-150px fixed-action',
        cellClassName: 'fixed-action',
        Cell: ({row}: {row: any}) => (
          <div
            className={`shipment-refund-badge-${row?.original?.refund?.status} px-4 py-2 rounded-pill`}
          >
            {!isEmpty(row.original.refund) ? (
              <span>
                {intl.formatMessage({
                  id: ShipmentRefundLabel[row.original.refund?.status] ?? 'PRINT_LABEL',
                })}
              </span>
            ) : (
              <div
                className={clsx('btn-group', {
                  'cursor-no-drop': row.original.isLoading,
                })}
              >
                <Button
                  className={clsx('btn btn-sm fs-6 rounded-2 w-150px', {
                    'btn-advanced-primary': ![
                      ShipmentRefund.SUBMITTED,
                      ShipmentRefund.REFUNDED,
                      ShipmentRefund.REJECTED,
                    ].includes(row.original.refund?.status),
                    'pe-none': row.original.isLoading,
                    'btn-light-danger': row.original.refund?.status === ShipmentRefund.SUBMITTED,
                    'btn-light-primary': row.original.refund?.status === ShipmentRefund.REFUNDED,
                    'btn-light-info': row.original.refund?.status === ShipmentRefund.REJECTED,
                  })}
                  event={row.original.refund === null ? () => handlePrintLabel(row.original) : null}
                  disabled={!isCreatedWithinLast30Days(row.original.created_at)}
                >
                  {intl.formatMessage({
                    id: ShipmentRefundLabel[row.original.refund?.status] ?? 'PRINT_LABEL',
                  })}
                </Button>
              </div>
            )}
          </div>
        ),
      },
      {
        id: TABLE_KEY_COLUMN.SUB_ACTIONS,
        cellClassName: 'fixed-column',
        Cell: ({row}: {row: any}) => {
          const isRefunded =
            row.original.refund?.status === ShipmentRefund.SUBMITTED ||
            row.original.refund?.status === ShipmentRefund.REFUNDED
          return (
            <DropdownButton
              loading={row.original.isLoading}
              list={[
                {
                  label: intl.formatMessage({id: 'REQUEST_REFUND'}),
                  action: () => {
                    setDataModal((prev) => ({...prev, confirmActionSwal: row.original.id}))
                    setShowModal((prev) => ({...prev, confirmActionSwal: 'REQUEST_REFUND'}))
                  },
                  className: 'cursor-pointer',
                  hidden: !routes.SHIPMENTS_REFUND.hasPermission || row.original.refund !== null,
                },
                {
                  label: intl.formatMessage({id: 'CREATE_CLAIM'}),
                  action: () => {
                    setDataModal((prev) => ({
                      ...prev,
                      createClaims: {
                        tracking_number: row.original?.tracking_code,
                        claim_recipient_phone:
                          formatPhoneNumber(row.original?.to_address?.phone) || '',
                        claim_recipient_email: row.original?.to_address?.email || '',
                      },
                    }))
                    setShowModal((prev) => ({...prev, createClaims: true}))
                  },
                  className: 'cursor-pointer',
                  hidden: !routes.CLAIMS_CREATE.hasPermission || isRefunded,
                },
                {
                  label: intl.formatMessage({id: 'PRINT_LABEL'}),
                  action: () => {
                    handlePrintLabel(row.original)
                  },
                  className: 'cursor-pointer',
                  hidden: isRefunded || !isCreatedWithinLast30Days(row.original.created_at),
                },
                {
                  label: intl.formatMessage({id: 'PRINT_PACKING_SLIP'}),
                  action: () => {
                    handlePrintPackingSlip(row.original)
                  },
                  className: 'cursor-pointer',
                  hidden: !routes.SHIPMENTS_PACKING_SLIP.hasPermission,
                },
              ]}
            />
          )
        },
      },
    ],
    [intl, currentUser, routes, handleViewOrder, carriers, handlePrintLabel, handlePrintPackingSlip]
  )

  const customColumns = useMemo(() => {
    if (columnsShow) {
      return showArrCustomColumns(columns, columnsShow)
    } else {
      return cloneDeep(columns).map((item: any) => {
        item.show = true
        return item
      })
    }
  }, [columns, columnsShow])

  const getSettings = useCallback(async () => {
    const settings = await SettingsService.settings({cancelToken: newCancelToken()})
    setSettings(settings)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newCancelToken])

  useEffect(() => {
    if (loadingSwitch) return

    setTable()

    return () => {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSwitch, setTable])

  useEffect(() => {
    if (loadingSwitch) return

    getSettings()

    return () => {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingSwitch, getSettings])

  const handleConfirmActionSwal = () => {
    switch (showModal.confirmActionSwal) {
      case 'REQUEST_REFUND':
        handleRefundLabel(dataModal.confirmActionSwal)
        break
    }
    setDataModal((prev) => ({...prev, confirmActionSwal: null}))
  }

  return (
    <>
      <ConfirmActionSwal
        show={!!showModal.confirmActionSwal}
        title={intl.formatMessage({id: 'REQUEST_REFUND'})}
        message={intl.formatMessage({id: 'ARE_YOU_SURE'})}
        messageCancel={intl.formatMessage({id: 'NO'})}
        handleCallBack={handleConfirmActionSwal}
        handleClose={() => setShowModal((prev) => ({...prev, confirmActionSwal: undefined}))}
      />
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}

      {showModal.noticePrintPackingSlip && (
        <NoticePrintPackingSlips
          show={showModal.noticePrintPackingSlip}
          handleClose={() => setShowModal((prev) => ({...prev, noticePrintPackingSlip: false}))}
          configModal={dataModal.noticePrintPackingSlip}
        />
      )}

      {showModal.packageInfoTable && (
        <PackageInfoModal
          show={showModal.packageInfoTable}
          data={dataModal.packageInfoTable}
          handleClose={() => setShowModal((prev) => ({...prev, packageInfoTable: false}))}
        />
      )}

      {showModal.trackingNumbers && (
        <TrackingNumbersModal
          show={showModal.trackingNumbers}
          handleClose={() => setShowModal((prev) => ({...prev, trackingNumbers: false}))}
          data={dataModal.trackingNumbers}
        />
      )}
      <TrackingNumberClaimModal
        show={showModal.createClaims}
        setShow={() => setShowModal((prev) => ({...prev, createClaims: false}))}
        dataInit={dataModal.createClaims}
      />

      <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
        {loadingFirst ? (
          <>
            <TableWrapper>
              <div className='table-content--inset-inner h-100'>
                <FilterOrderSkeleton />
                <TableBody>
                  <TableSkeleton />
                </TableBody>
              </div>
            </TableWrapper>
          </>
        ) : (
          <>
            <TableWrapper>
              <div className='table-content--inset-inner h-100'>
                <ShipmentFilter
                  selectedListId={selectedListId}
                  reloadTable={() => {
                    setTable()
                    setMeterChange(true)
                  }}
                  dataSettings={settings}
                />
                <TableBody
                  height={`calc(100vh - ${
                    OUTER_HEIGHT_SHIPMENT - SHIPMENT_PADDING_HEIGHT + topAlertHeight
                  }px)`}
                >
                  {!responseData?.pagination?.total ? (
                    emptyStateContent
                  ) : (
                    <Table
                      columns={customColumns}
                      data={tableData}
                      pagination={responseData?.pagination || {}}
                      tbodyClass='text-gray-600 fw-bold'
                      usePagination
                      useCheckBox
                      callBackSetData={(selected) => {
                        setSelectedListId(selected)
                      }}
                      customColumn={{show: true, name: CUSTOM_COLUMN_NAME?.SHIPMENT_PAGE}}
                    />
                  )}
                </TableBody>
              </div>
            </TableWrapper>
          </>
        )}
      </CSSTransition>
    </>
  )
}

export {ShipmentPage}
